import { StandaloneProseProps } from '@/components/Prose';
import { useClassName } from '@/styles/ContentCoverBox';
import { Component, ComponentProps } from '@/types/component';
import mergeProps from 'lib/utils/mergeProps';
import { standaloneComponent } from 'modules/dynamic-components';
import React, { ReactElement, useEffect, useRef, useState } from 'react';

export interface CoverBoxBodyProps extends ComponentProps<'div'> {
  expand?: ReactElement;
  lineClamp?: number;
  markup: string;
}

const Prose = standaloneComponent<StandaloneProseProps>('ElementProse');

export const CoverBoxBodyComponent: Component<CoverBoxBodyProps> = ({
  children,
  expand: Expand,
  lineClamp = 2,
  markup,
  ...props
}) => {
  const [isClamped, setIsClamped] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  const toggleExpand = () => {
    setIsClamped(isExpanded);
    setIsExpanded((prevState) => !prevState);
  };

  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onResize = () => {
      setIsClamped((ref.current?.scrollHeight ?? 0) > (ref.current?.clientHeight ?? 0));
    };

    onResize();
    window.addEventListener('resize', onResize);

    return () => {
      window.removeEventListener('resize', onResize);
    };
  }, []);

  const lineClampStyles: CoverBoxBodyProps['style'] = {
    overflow: 'hidden',
    display: '-webkit-box',
    WebkitBoxOrient: 'vertical',
    WebkitLineClamp: lineClamp,
  };

  const className = useClassName('body', props);
  const proseClassName = useClassName('body_prose', props);

  return (
    <div
      {...{ ref }}
      {...mergeProps(
        { className, style: lineClampStyles, 'data-expanded': `${isExpanded}`, 'data-clamped': `${isClamped}` },
        props
      )}
    >
      <Prose options={{ className: proseClassName }} {...{ markup }} />

      {children}

      {Expand &&
        React.cloneElement(Expand, {
          onClick: toggleExpand,
        })}
    </div>
  );
};
